import React, { useState, useEffect, useCallback } from 'react';
import {
  Headphones, AlertCircle, Clock, CheckCircle,
  BarChart3, RefreshCw, Filter, Plus,
} from 'lucide-react';
import {
  PageHeader,
  AppButton,
  SearchInput,
  FilterBar,
  FilterSelect,
  StatsCard,
  Tabs,
  TabsList,
  TabsTrigger,
  Paginator,
} from '../ui';
import TicketTable from './TicketTable';
import CommonToaster from '../Common/CommonToaster';
import ReportIssueModal from './ReportIssueModal';
import apiClient from '../../utils/apiClient';
import './Support.scss';

const statusTabs = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const SupportDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [institutes, setInstitutes] = useState([]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: 20 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (tenantFilter) params.tenantId = tenantFilter;

      const { data } = await apiClient.get('/support/admin/tickets', { params });
      setTickets(data.tickets || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || 0);
      if (data.stats) setStats(data.stats);
    } catch {
      setToast({ show: true, message: 'Failed to load tickets.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter, tenantFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  useEffect(() => {
    const fetchInstitutes = async () => {
      try {
        const { data } = await apiClient.get('/sa/institutes');
        setInstitutes(data.institutes || []);
      } catch { /* non-critical */ }
    };
    fetchInstitutes();
  }, []);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalAll = stats.total || (stats.open + stats.in_progress + stats.resolved + stats.closed) || 0;
  const activeFilterCount = [priorityFilter, tenantFilter].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setTenantFilter('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const getTabCount = (key) => {
    if (!key) return totalAll;
    return stats[key] || 0;
  };

  const statusTabItems = statusTabs.map(({ key, label }) => ({
    key,
    label: `${label} (${getTabCount(key)})`,
  }));

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const instituteOptions = [
    { value: '', label: 'All Institutes' },
    ...institutes.map((inst) => ({ value: inst._id, label: inst.name })),
  ];

  return (
    <div className="sd-page">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <PageHeader
        icon={Headphones}
        title="Support Dashboard"
        description="Manage all support tickets across institutes"
        actions={
          <div className="d-flex gap-2">
            <AppButton size="sm" className="d-flex align-items-center gap-1" onClick={() => setShowCreateModal(true)}>
              <Plus size={15} /> New Ticket
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={fetchTickets} disabled={loading} title="Refresh">
              <RefreshCw size={15} className={loading ? 'sp-spin' : ''} />
            </AppButton>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="sp-stats-grid">
        <StatsCard title="Total Tickets" value={totalAll} subtitle="All time" icon={Headphones} iconColor="blue" gradient="blue" />
        <StatsCard title="Open" value={stats.open || 0} subtitle="Awaiting response" icon={AlertCircle} iconColor="amber" gradient="amber" />
        <StatsCard title="In Progress" value={stats.in_progress || 0} subtitle="Being worked on" icon={Clock} iconColor="blue" gradient="cyan" />
        <StatsCard title="Resolved" value={stats.resolved || 0} subtitle="Successfully resolved" icon={CheckCircle} iconColor="emerald" gradient="emerald" />
        <StatsCard title="Avg Response" value="-" subtitle="First response time" icon={BarChart3} iconColor="violet" gradient="violet" />
      </div>

      {/* Tabs */}
      <Tabs value={statusFilter} onChange={(value) => { setStatusFilter(value); setPage(1); }}>
        <TabsList>
          {statusTabItems.map((tab) => (
            <TabsTrigger key={tab.key || 'all'} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Search + Filters */}
      <FilterBar
        className="sp-filter-row"
        actions={activeFilterCount > 0 ? (
          <AppButton variant="outline" size="sm" className="sp-filter-clear" onClick={clearFilters}>
            <Filter size={13} /> Clear ({activeFilterCount})
          </AppButton>
        ) : null}
      >
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search tickets, students, or IDs..."
          className="sp-enterprise-search"
        />
        <FilterSelect
          value={priorityFilter}
          onChange={(value) => { setPriorityFilter(value); setPage(1); }}
          options={priorityOptions}
        />
        <FilterSelect
          value={tenantFilter}
          onChange={(value) => { setTenantFilter(value); setPage(1); }}
          options={instituteOptions}
        />
      </FilterBar>

      {/* Results */}
      <div className="sp-results-bar">
        <span className="sp-results-bar__count">
          {loading ? 'Loading...' : `${totalCount} ticket${totalCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      {!loading && tickets.length === 0 ? (
        <div className="sp-empty">
          <Headphones size={48} strokeWidth={1.2} />
          <h3>No tickets found</h3>
          <p>{activeFilterCount > 0 || statusFilter ? 'Try adjusting your filters' : 'All clear! No issues reported yet.'}</p>
        </div>
      ) : (
        <TicketTable
          tickets={tickets}
          loading={loading}
          basePath="/sa/support"
          showTenant
          canManage
        />
      )}

      {totalPages > 1 && (
        <div className="sp-pagination">
          <Paginator
            page={page}
            totalPages={totalPages}
            total={totalCount}
            pageSize={20}
            onChange={setPage}
            infoLabel="tickets"
          />
        </div>
      )}

      {/* Create ticket on behalf of an institute */}
      <ReportIssueModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        isTenantAdmin
        institutes={institutes}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchTickets();
          setToast({ show: true, message: 'Ticket created successfully.', type: 'success' });
        }}
      />
    </div>
  );
};

export default SupportDashboard;
